import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Input, Label, Modal, TextField } from '@heroui/react';
import type { ChartAccount, FixedAsset, LocaleCode } from '@/api/models';
import { DatePickerField } from '@/components/datePickerField';
import { formatGel } from '@/pages/accounting/books/chartOfAccounts/utils';
import { JournalEntryModal } from '@/pages/accounting/journalEntry';
import { resolveLabel } from '@/pages/accounting/statements/statementMeta';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { DISPOSAL_DIRECTION_KEY } from '../../fixedAssetsMeta';
import { AssetAccountPicker } from '../assetFormDrawer';
import { useDispose } from './useDispose';

type DisposeDialogProps = {
    /** The asset being disposed; `null` closes the dialog. */
    asset: FixedAsset | null;
    accounts: ChartAccount[];
    locale: LocaleCode;
    onClose: () => void;
};

const Fact: React.FC<{ label: string; value: string; slot: string }> = ({
    label,
    value,
    slot,
}) => (
    <div className="flex flex-col gap-0.5">
        <dt className="text-xs font-medium uppercase tracking-wide text-muted">
            {label}
        </dt>
        <dd
            className="text-sm tabular-nums text-foreground"
            {...getNodeTestIdProp(`fixed-assets-dispose-${slot}`)}
        >
            {value}
        </dd>
    </div>
);

/**
 * Disposal confirmation.
 *
 * States what it does before it does it — removes the cost and the accumulated
 * depreciation from the books and books the difference against the proceeds — and
 * shows the resulting **gain or loss live**, as the proceeds are typed. Confirming
 * does not dispose the asset: it prepares the draft entry and opens it in the
 * Journal Entry form. The asset is marked disposed only after that entry is
 * posted, which the dialog says in as many words.
 */
export const DisposeDialog: React.FC<DisposeDialogProps> = ({
    asset,
    accounts,
    locale,
    onClose,
}) => {
    const { t } = useTranslation('pages');
    const model = useDispose({ asset, accounts, locale, onClose });

    const assetLabel = asset ? resolveLabel(asset.name, locale, asset.id) : '';

    return (
        <>
            <Modal
                isOpen={asset !== null && model.draft === null}
                onOpenChange={(open) => !open && onClose()}
            >
                <Modal.Backdrop>
                    <Modal.Container size="md">
                        <Modal.Dialog
                            className="w-full max-w-lg"
                            {...getNodeTestIdProp(
                                'fixed-assets-dispose-dialog',
                            )}
                        >
                            <Modal.CloseTrigger />
                            <Modal.Header>
                                <Modal.Heading>
                                    {t('fixedAssets.dispose.title', {
                                        name: assetLabel,
                                    })}
                                </Modal.Heading>
                                <p className="text-sm text-muted">
                                    {t('fixedAssets.dispose.explainer')}
                                </p>
                            </Modal.Header>

                            <Modal.Body className="flex flex-col gap-4 pb-2">
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <DatePickerField
                                        id="fixed-assets-dispose-date"
                                        label={t(
                                            'fixedAssets.dispose.fields.date',
                                        )}
                                        name="disposalDate"
                                        onChange={model.onDisposalDateChange}
                                        value={model.disposalDate}
                                        {...(model.dateErrorKey
                                            ? { error: t(model.dateErrorKey) }
                                            : {})}
                                    />
                                    <TextField
                                        className="flex flex-col gap-1"
                                        isInvalid={
                                            model.proceedsErrorKey !== null
                                        }
                                        onChange={model.onProceedsChange}
                                        value={model.proceeds}
                                        {...getNodeTestIdProp(
                                            'fixed-assets-dispose-proceeds',
                                        )}
                                    >
                                        <Label>
                                            {t(
                                                'fixedAssets.dispose.fields.proceeds',
                                            )}
                                        </Label>
                                        <Input
                                            className="text-right tabular-nums"
                                            inputMode="decimal"
                                            placeholder="0.00"
                                        />
                                        {model.proceedsErrorKey ? (
                                            <span className="text-xs text-danger">
                                                {t(model.proceedsErrorKey)}
                                            </span>
                                        ) : null}
                                    </TextField>
                                </div>

                                <div className="flex flex-col gap-1">
                                    <Label id="fixed-assets-dispose-account-label">
                                        {t(
                                            'fixedAssets.dispose.fields.proceedsAccount',
                                        )}
                                    </Label>
                                    <AssetAccountPicker
                                        ariaLabel={t(
                                            'fixedAssets.dispose.fields.proceedsAccount',
                                        )}
                                        onChange={model.onProceedsAccountChange}
                                        options={model.accountOptions}
                                        placeholder={t(
                                            'fixedAssets.form.fields.fundingPlaceholder',
                                        )}
                                        slot="proceeds"
                                        value={model.proceedsAccountId}
                                    />
                                </div>

                                <dl className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-xl bg-surface-secondary p-4">
                                    <Fact
                                        label={t(
                                            'fixedAssets.dispose.netBookValue',
                                        )}
                                        slot="nbv"
                                        value={formatGel(
                                            model.netBookValueTetri,
                                        )}
                                    />
                                    <Fact
                                        label={t(
                                            'fixedAssets.dispose.fields.proceeds',
                                        )}
                                        slot="proceeds-value"
                                        value={formatGel(model.proceedsTetri)}
                                    />
                                </dl>

                                <div
                                    className={cn(
                                        'flex items-center justify-between gap-3 rounded-xl px-4 py-3',
                                        model.direction === 'GAIN'
                                            ? 'bg-success-soft text-success-soft-foreground'
                                            : model.direction === 'LOSS'
                                              ? 'bg-warning-soft text-warning-soft-foreground'
                                              : 'bg-surface text-muted',
                                    )}
                                    {...getNodeTestIdProp(
                                        'fixed-assets-dispose-result',
                                    )}
                                >
                                    <span className="text-sm font-semibold">
                                        {t(
                                            DISPOSAL_DIRECTION_KEY[
                                                model.direction
                                            ],
                                        )}
                                    </span>
                                    <span className="text-lg font-bold tabular-nums">
                                        {formatGel(
                                            Math.abs(model.gainOrLossTetri),
                                        )}
                                    </span>
                                </div>

                                <p className="flex items-start gap-2 text-xs text-muted">
                                    <Icon
                                        aria-hidden
                                        className="mt-0.5 shrink-0"
                                        icon="gravity-ui:circle-info"
                                    />
                                    {t('fixedAssets.dispose.postNote')}
                                </p>

                                {/*
                                  Server advisories. **Non-blocking by design**:
                                  `DISPOSAL_LOSS_DEBITS_REVENUE` fires because
                                  account 4300 is typed Revenue, so a loss debits
                                  revenue. That is a real accounting question
                                  still open with the accountants — surfaced
                                  rather than hidden, and never a validation
                                  error, because a client that suppressed it
                                  would be answering it by omission.
                                */}
                                {model.warnings.length > 0 ? (
                                    <div
                                        className="flex flex-col gap-2 rounded-lg bg-warning-soft p-3"
                                        {...getNodeTestIdProp(
                                            'fixed-assets-dispose-warnings',
                                        )}
                                    >
                                        {model.warnings.map((warning) => (
                                            <p
                                                className="flex items-start gap-2 text-xs text-warning-soft-foreground"
                                                key={`${warning.rule}-${warning.accountCode ?? ''}`}
                                            >
                                                <Icon
                                                    aria-hidden
                                                    className="mt-0.5 shrink-0"
                                                    icon="gravity-ui:triangle-exclamation"
                                                />
                                                {warning.message}
                                            </p>
                                        ))}
                                    </div>
                                ) : null}

                                {model.prepareError ? (
                                    <p
                                        className="text-sm text-danger"
                                        {...getNodeTestIdProp(
                                            'fixed-assets-dispose-error',
                                        )}
                                    >
                                        {model.prepareError}
                                    </p>
                                ) : null}
                            </Modal.Body>

                            <Modal.Footer>
                                <Button
                                    onPress={onClose}
                                    variant="tertiary"
                                    {...getNodeTestIdProp(
                                        'fixed-assets-dispose-cancel',
                                    )}
                                >
                                    {t('fixedAssets.actions.cancel')}
                                </Button>
                                <Button
                                    isDisabled={!model.isValid}
                                    isPending={
                                        model.isPreviewing || model.isCommitting
                                    }
                                    onPress={model.onPrimaryAction}
                                    variant="danger-soft"
                                    {...getNodeTestIdProp(
                                        'fixed-assets-dispose-confirm',
                                    )}
                                >
                                    {t(model.primaryLabelKey)}
                                </Button>
                            </Modal.Footer>
                        </Modal.Dialog>
                    </Modal.Container>
                </Modal.Backdrop>
            </Modal>

            <JournalEntryModal
                entry={model.draft}
                isOpen={model.draft !== null}
                onClose={model.onDraftClose}
                onSaved={model.onDraftSaved}
            />

            {/*
              The state §3 asks for: the terms are recorded, the entry is
              drafted, and the asset is still ACTIVE — because its position moves
              when the entry posts, not when the terms are recorded. Saying
              "Disposed" here would claim a ledger position that does not exist.
            */}
            <Modal
                isOpen={model.pending !== null}
                onOpenChange={(open) => !open && model.onPendingDismiss()}
            >
                <Modal.Backdrop>
                    <Modal.Container size="sm">
                        <Modal.Dialog
                            className="w-full max-w-lg"
                            {...getNodeTestIdProp(
                                'fixed-assets-disposal-pending',
                            )}
                        >
                            <Modal.CloseTrigger />
                            <Modal.Header>
                                <Modal.Heading>
                                    <span className="flex items-center gap-2">
                                        <Icon
                                            aria-hidden
                                            icon="gravity-ui:clock"
                                        />
                                        {t('fixedAssets.dispose.pending.title')}
                                    </span>
                                </Modal.Heading>
                            </Modal.Header>
                            <Modal.Body className="flex flex-col gap-3 pb-6">
                                <p
                                    className="text-sm text-foreground"
                                    {...getNodeTestIdProp(
                                        'fixed-assets-disposal-pending-body',
                                    )}
                                >
                                    {t('fixedAssets.dispose.pending.body', {
                                        asset: model.pending?.assetName ?? '',
                                    })}
                                </p>
                                <p className="text-xs text-muted">
                                    {t(
                                        'fixedAssets.dispose.pending.stillActive',
                                    )}
                                </p>
                                <div className="flex justify-end gap-2">
                                    <Button
                                        onPress={model.onPendingDismiss}
                                        variant="tertiary"
                                        {...getNodeTestIdProp(
                                            'fixed-assets-disposal-pending-close',
                                        )}
                                    >
                                        {t('fixedAssets.actions.close')}
                                    </Button>
                                    <Button
                                        onPress={model.onOpenPendingEntry}
                                        variant="primary"
                                        {...getNodeTestIdProp(
                                            'fixed-assets-disposal-pending-open',
                                        )}
                                    >
                                        {model.pending?.entryNumber
                                            ? t(
                                                  'fixedAssets.dispose.pending.openNumbered',
                                                  {
                                                      number: model.pending
                                                          .entryNumber,
                                                  },
                                              )
                                            : t(
                                                  'fixedAssets.dispose.pending.open',
                                              )}
                                    </Button>
                                </div>
                            </Modal.Body>
                        </Modal.Dialog>
                    </Modal.Container>
                </Modal.Backdrop>
            </Modal>
        </>
    );
};
